import { Route, Routes, useParams } from 'react-router-dom'
import { ProtectedRoute } from './components/Auth/ProtectedRoute'
import { Helmet } from 'react-helmet-async'
import AudiencePage from './pages/Audience/AudiencePage'
import RequestingTypePage from './pages/RequestingType/RequestingType'
import DateAndTimesPage from './pages/DateAndTimes/DateAndTimesPage'
import RoomBooking from './pages/RoomBooking/RoomBooking'
import EtaCalendarPage from './pages/EtaCalendar/EtaCalendarPage'
import EventInfoPage from './pages/EventInfo/EventInfoPage'
import FeaturesAndRequestsPage from './pages/FeaturesAndRequests/FeaturesAndRequestsPage'
import ReviewAndSubmitPage from './pages/ReviewAndSubmit/ReviewAndSubmitPage'
import SpeakerAndStaffSupportPage from './pages/SpeakesAndSupportStaff/SpeakesAndSupportStaffPage'
import { AdminScheduling } from './pages/Workflow/AdminScheduling/AdminScheduling'
import { Delete } from './pages/Workflow/Delete'
import { PendingAccount } from './pages/Workflow/PendingAccount'
import { Users } from './pages/Workflow/Users'
import { View } from './pages/Workflow/View/View'
import { ViewAndEdit } from './pages/Workflow/ViewAndEdit'
import { StartCloseout } from './pages/EventCloseout/StartCloseout'
import { HighlightsAndActionItems } from './pages/EventCloseout/HighlightsAndActionItems'
import { RecordFeedback } from './pages/EventCloseout/RecordFeedback'
import { Page3 } from './pages/EventCloseout/Page3'
import Help from './pages/Help/Help'
import { Accounts } from './pages/Accounts/Accounts'
import { Events } from './pages/Workflow/Events'
import EventReview from './pages/EventReview/EventReview'
import AdminBooking from './pages/AdminBooking/AdminBooking'
import Maintenance from './pages/Maintenance/Maintenance'

const DynamicTitleRoute = ({ title, children }: { title: string, children: React.ReactNode }) => {
  const params = useParams();
  const id = params.id;
  const displayTitle = id ? `${title} (${id}) - ETA TA Event Planner` : `${title} - ETA TA Event Planner`;

  return (
    <>
      <Helmet>
        <title>{displayTitle}</title>
      </Helmet>
      {children}
    </>
  );
};

const PageRoute = () => {
  return (
    <Routes>
      <Route path='/' element={
        <DynamicTitleRoute title="Dashboard">
          <EtaCalendarPage />
        </DynamicTitleRoute>
      } />
      <Route path='/maintenance' element={
        <DynamicTitleRoute title="Maintenance">
          <Maintenance />
        </DynamicTitleRoute>
      } />
      <Route
        path='/event/requestingType'
        element={
          <DynamicTitleRoute title="Request Event">
            <ProtectedRoute outlet={<RequestingTypePage />} />
          </DynamicTitleRoute>
        }
      />
      <Route
        path='/event/requestingType/:id'
        element={
          <DynamicTitleRoute title="Event Request">
            <ProtectedRoute outlet={<RequestingTypePage />} />
          </DynamicTitleRoute>
        }
      />
      <Route
        path='/event/info'
        element={
          <DynamicTitleRoute title="Event Information">
            <ProtectedRoute outlet={<EventInfoPage />} />
          </DynamicTitleRoute>
        }
      />
      <Route
        path='/event/info/:id'
        element={
          <DynamicTitleRoute title="Event Information">
            <ProtectedRoute outlet={<EventInfoPage />} />
          </DynamicTitleRoute>
        }
      />
      <Route
        path='/event/staff/:id'
        element={
          <DynamicTitleRoute title="Staff & Support">
            <ProtectedRoute outlet={<SpeakerAndStaffSupportPage />} />
          </DynamicTitleRoute>
        }
      />
      <Route
        path='/event/audience/:id'
        element={
          <DynamicTitleRoute title="Audience">
            <ProtectedRoute outlet={<AudiencePage />} />
          </DynamicTitleRoute>
        }
      />
      <Route
        path='/event/dateAndTime/:id'
        element={
          <DynamicTitleRoute title="Date & Time">
            <ProtectedRoute outlet={<RoomBooking />} />
          </DynamicTitleRoute>
        }
      />
      <Route
        path='/event/featuresAndRequests/:id'
        element={
          <DynamicTitleRoute title="Features & Requests">
            <ProtectedRoute outlet={<FeaturesAndRequestsPage />} />
          </DynamicTitleRoute>
        }
      />
      <Route
        path='/event/review/:id'
        element={
          <DynamicTitleRoute title="Review Event">
            <ProtectedRoute outlet={<EventReview />} />
          </DynamicTitleRoute>
        }
      />
      <Route
        path='/events'
        element={
          <DynamicTitleRoute title="Events">
            <ProtectedRoute outlet={<Events />} />
          </DynamicTitleRoute>
        }
      />
      <Route
        path='/admin'
        element={
          <DynamicTitleRoute title="Admin Dashboard">
            <ProtectedRoute outlet={<Events />} />
          </DynamicTitleRoute>
        }
      />
      <Route
        path='/admin/events'
        element={
          <DynamicTitleRoute title="Admin Events">
            <ProtectedRoute outlet={<Events />} />
          </DynamicTitleRoute>
        }
      />
      <Route
        path='/admin/event/:id'
        element={
          <DynamicTitleRoute title="Review Event">
            <ProtectedRoute outlet={<EventReview />} />
          </DynamicTitleRoute>
        }
      />
      <Route
        path='/admin/booking/'
        element={
          <DynamicTitleRoute title="Admin Scheduling">
            <ProtectedRoute outlet={<AdminBooking />} />
          </DynamicTitleRoute>
        }
      />
      <Route
        path='/admin/booking/scheduler'
        element={
          <DynamicTitleRoute title="Admin Scheduler">
            <ProtectedRoute outlet={<AdminBooking />} />
          </DynamicTitleRoute>
        }
      />
      <Route
        path='/admin/booking/editor'
        element={
          <DynamicTitleRoute title="Admin Editor">
            <ProtectedRoute outlet={<AdminBooking />} />
          </DynamicTitleRoute>
        }
      />
      <Route
        path='/admin/accounts/'
        element={
          <DynamicTitleRoute title="Admin Accounts">
            <ProtectedRoute outlet={<Accounts />} />
          </DynamicTitleRoute>
        }
      />
      <Route
        path='/admin/accounts/user'
        element={
          <DynamicTitleRoute title="User Accounts">
            <ProtectedRoute outlet={<Accounts />} />
          </DynamicTitleRoute>
        }
      />
      <Route
        path='/admin/accounts/programOffices'
        element={
          <DynamicTitleRoute title="Program Offices">
            <ProtectedRoute outlet={<Accounts />} />
          </DynamicTitleRoute>
        }
      />
      {/* <Route
        path='/review/:id'
        element={<ProtectedRoute outlet={<ReviewAndSubmitPage />} />}
      /> */}
      {/* <Route path='/view/:id'
        element={<ProtectedRoute outlet={<View />} />}
      /> */}
      {/* <Route
        path='/admin-old'
        element={<ProtectedRoute outlet={<AdminScheduling />} />}
      /> */}
      {/* <Route path='/delete' element={<ProtectedRoute outlet={<Delete />} />} />
      <Route
        path='/pendingAccount'
        element={<ProtectedRoute outlet={<PendingAccount />} />}
      />
      <Route path='/users' element={<ProtectedRoute outlet={<Users />} />} />
      <Route
        path='/viewandedit'
        element={<ProtectedRoute outlet={<ViewAndEdit />} />}
      /> */}
      {/* <Route path='/dateAndTime/' element={<DateAndTimesPage />} />
      <Route path='/startCloseout/' element={<StartCloseout />} />
      <Route
        path='/highlightsAndActionItems/'
        element={<HighlightsAndActionItems />}
      />
      <Route path='/recordFeedback/' element={<RecordFeedback />} />
      <Route path='/page3/' element={<Page3 />} /> */}
      <Route path='/support' element={
        <DynamicTitleRoute title="Support: Help and Resources">
          <Help />
        </DynamicTitleRoute>
      } />
      <Route path='*' element={
        <DynamicTitleRoute title="Dashboard">
          <EtaCalendarPage />
        </DynamicTitleRoute>
      } />
    </Routes>
  )
}

export default PageRoute
